import { Anchor, Badge, Button, Card, Container, Group, Stack, Table, Text, ThemeIcon, Title } from '@mantine/core'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { LuAnchor, LuUsers } from 'react-icons/lu'
import { CustomerAvatar } from '@/frontend/components/marina/CustomerAvatar'
import { DataTable } from '@/frontend/components/marina/DataTable'
import { KindBadge } from '@/frontend/components/marina/KindBadge'
import { useCanWrite } from '@/frontend/hooks/useAuth'
import { useCustomers } from '@/frontend/hooks/useCustomers'

export const Route = createFileRoute('/dashboard/accounts/customers/')({
  component: CustomersList,
})

function CustomersList() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const canWrite = useCanWrite()
  const [search, setSearch] = useState('')
  const [query, setQuery] = useState('')
  const [page, setPage] = useState(1)
  const pageSize = 15

  const { data, isLoading } = useCustomers({ q: query, page, pageSize })
  const items = data?.items ?? []
  const total = data?.total ?? 0

  return (
    <Container size="xl" py="md">
      <Stack gap="md">
        <Group justify="space-between" align="flex-start" wrap="nowrap">
          <Group gap="sm" align="center">
            <ThemeIcon variant="light" color="laut" size="lg" radius="md">
              <LuUsers size={18} />
            </ThemeIcon>
            <div>
              <Title order={2} ff="var(--font-display)" fw={600}>
                {t('page.accounts.customers.title')}
              </Title>
              <Text size="xs" c="dimmed">
                {t('page.accounts.customers.subtitle')}
              </Text>
            </div>
          </Group>
          {canWrite && (
            <Button color="karang" size="sm" onClick={() => navigate({ to: '/dashboard/accounts/customers/new' })}>
              {t('page.accounts.customers.btn_new')}
            </Button>
          )}
        </Group>

        <Group justify="flex-end">
          <input
            type="search"
            value={search}
            placeholder={t('page.accounts.customers.placeholder')}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                setQuery(search)
                setPage(1)
              }
            }}
            style={{
              fontSize: '0.8rem',
              padding: '6px 12px',
              borderRadius: 8,
              border: '1px solid var(--mantine-color-default-border)',
              background: 'var(--app-card-bg)',
              color: 'inherit',
              width: 260,
              outline: 'none',
            }}
          />
        </Group>

        <Card radius="md" padding="lg">
          <DataTable
            isEmpty={!isLoading && items.length === 0}
            emptyLabel={t('page.accounts.customers.empty')}
            page={page}
            pageSize={pageSize}
            total={total}
            onPageChange={setPage}
            columns={
              <>
                <Table.Th>{t('page.accounts.customers.col_customer')}</Table.Th>
                <Table.Th>{t('page.accounts.customers.col_boats')}</Table.Th>
                <Table.Th>{t('page.accounts.customers.col_category')}</Table.Th>
                <Table.Th ta="center">{t('page.accounts.customers.col_status')}</Table.Th>
                <Table.Th ta="right">{t('page.accounts.customers.col_actions')}</Table.Th>
              </>
            }
          >
            {items.map((c) => {
              const fullName = `${c.firstName} ${c.lastName}`.trim()
              const extraCount = Math.max((c._count?.boats ?? 0) - 1, 0)
              const firstBoat = c.boats?.[0]?.name
              return (
                <Table.Tr
                  key={c.id}
                  style={{ cursor: 'pointer' }}
                  onClick={() => navigate({ to: `/dashboard/accounts/customers/${c.id}` as any })}
                >
                  <Table.Td>
                    <Group gap="sm" wrap="nowrap">
                      <CustomerAvatar name={fullName} email={c.email} size={36} />
                      <div style={{ minWidth: 0 }}>
                        <Text size="sm" fw={600} truncate>
                          {fullName}
                        </Text>
                        <Text size="xs" c="dimmed" truncate>
                          {c.email || '—'}
                        </Text>
                      </div>
                    </Group>
                  </Table.Td>
                  <Table.Td>
                    {firstBoat ? (
                      <Group gap={4} wrap="nowrap">
                        <Text size="sm" truncate>
                          {firstBoat}
                        </Text>
                        {extraCount > 0 && (
                          <Badge size="xs" variant="light" color="gray">
                            +{extraCount}
                          </Badge>
                        )}
                      </Group>
                    ) : (
                      <Text size="sm" c="dimmed">
                        —
                      </Text>
                    )}
                  </Table.Td>
                  <Table.Td>
                    <KindBadge category={c.category} />
                  </Table.Td>
                  <Table.Td ta="center">
                    {c.checkedIn ? (
                      <Badge size="xs" color="laut" variant="light" leftSection={<LuAnchor size={9} />}>
                        {t('page.accounts.customers.badge_in_marina')}
                      </Badge>
                    ) : (
                      <Text size="xs" c="dimmed">
                        —
                      </Text>
                    )}
                  </Table.Td>
                  <Table.Td ta="right" onClick={(e) => e.stopPropagation()}>
                    <Anchor
                      component={Link}
                      to={`/dashboard/accounts/customers/${c.id}` as any}
                      c="laut"
                      size="sm"
                      fw={500}
                    >
                      {t('common.see_more')}
                    </Anchor>
                  </Table.Td>
                </Table.Tr>
              )
            })}
          </DataTable>
        </Card>
      </Stack>
    </Container>
  )
}
